import {
  EnumTagEntityTypes,
  EnumTagGroupType,
  TagByIdQuery,
  TagGroup,
  TagManyWithCountsQuery,
  TagSuggestionManyQuery,
  useTagManyWithCountsQuery,
  useTagRefreshCountsLazyQuery,
  useTagSuggestionManyQuery,
} from "gql/generated";
import _ from "lodash";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTagGroupContext } from "shared/contextProviders/tagGroups";
import { getMultilingualString } from "shared/utils";

export enum TagManagerFilter {
  BusinessType = EnumTagGroupType.BusinessType,
  Global = EnumTagGroupType.Global,
  Amenity = EnumTagGroupType.Amenity,
  GlobalEvent = EnumTagGroupType.GlobalEvent,
  Suggestions = "SUGGESTIONS",
}

export type TagGroupSuggestions = {
  _id: "suggestions";
  key: TagManagerFilter.Suggestions;
  name: { en: "Suggestions" };
  type: TagManagerFilter.Suggestions;
};

export type TagGroupWithTags = {
  tagGroup: TagGroup | TagGroupSuggestions;
  tags:
    | TagManyWithCountsQuery["tagMany"]
    | TagSuggestionManyQuery["tagSuggestionMany"];
};

export const useTagManager = () => {
  const { t } = useTranslation("adminland", {
    keyPrefix: "PAGE.TAG_MANAGER",
  });

  const { getTagGroups } = useTagGroupContext();

  // Get all of the tag groups from the tagGroups context
  const tagGroups = useMemo(() => {
    return getTagGroups({
      type: [
        EnumTagGroupType.BusinessType,
        EnumTagGroupType.Global,
        EnumTagGroupType.Amenity,
        EnumTagGroupType.GlobalEvent,
      ],
    });
  }, [getTagGroups]);

  // The options for the category dropdown in the flyouts
  const flyoutCategoryOptions = useMemo(() => {
    return _.orderBy(
      tagGroups.map(tagGroup => ({
        value: tagGroup._id,
        label:
          t(`SEARCH_AND_FILTER.${tagGroup.type}`) +
          " > " +
          getMultilingualString(tagGroup.name),
        key: tagGroup.key,
        type: tagGroup.type,
      })),
      "label",
      "asc",
    );
  }, [t, tagGroups]);

  // Get all non-organization tags from the tags collection
  const {
    data: tags,
    loading: tagsLoading,
    error: tagsError,
    refetch: refetchTags,
  } = useTagManyWithCountsQuery({
    variables: {
      filter: {
        tagGroup: tagGroups.map(tagGroup => tagGroup._id),
        entityTypes: [
          EnumTagEntityTypes.Event,
          EnumTagEntityTypes.Operator,
          EnumTagEntityTypes.Product,
        ],
      },
      limit: 10000,
    },
    // Add this so the refetch will trigger the useEffect below
    notifyOnNetworkStatusChange: true,
  });

  // Get all tags from the tagSuggestions collection
  const {
    data: tagSuggestions,
    loading: tagSuggestionsLoading,
    error: tagSuggestionsError,
  } = useTagSuggestionManyQuery({
    variables: {
      limit: 10000,
    },
  });

  // Store the search value and selected filter button
  const [searchValue, setSearchValue] = useState("");
  const [filter, setFilter] = useState<TagManagerFilter>(
    // Note: This will be overridden to TagManagerFilter.BusinessType in the useEffect that
    // joins these tags with the tag suggestions below
    TagManagerFilter.Suggestions,
  );

  // Update the search value and apply the filters
  const handleSearchValueChange = (e: ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);

  // Update the selected filter button and apply the filters
  const handleFilterChange = (filter: TagManagerFilter) => setFilter(filter);

  const [openModal, setOpenModal] = useState<
    "addTag" | "viewTag" | "approveTag" | null
  >(null);
  const [initialTagGroup, setInitialTagGroup] = useState<TagGroup | null>(null);
  const [selectedTag, setSelectedTag] = useState<
    | TagManyWithCountsQuery["tagMany"][0]
    | TagSuggestionManyQuery["tagSuggestionMany"][0]
    | null
  >(null);

  /**
   * Open the Suggest tag modal
   * - If no tag group is provided, the drop-down to select the tag group will be editable
   * - Otherwise, that will be pre-selected and disabled
   * @param tagGroup
   */
  const handleAddTag = (tagGroup: TagGroup | TagGroupSuggestions | null) => {
    setOpenModal("addTag");
    setInitialTagGroup(
      tagGroup?.key !== TagManagerFilter.Suggestions
        ? (tagGroup as TagGroup)
        : null,
    );
  };

  /**
   * Open the View tag modal OR approve tag suggestion modal
   *
   * @param tag
   */
  const handleViewTag = (
    tag:
      | TagManyWithCountsQuery["tagMany"][0]
      | TagSuggestionManyQuery["tagSuggestionMany"][0],
  ) => {
    setSelectedTag(tag);
    switch (tag.__typename) {
      case "Tag":
        setOpenModal("viewTag");
        break;
      case "TagSuggestion":
        setOpenModal("approveTag");
        break;
    }
  };

  // Reset the modal states when the modal is closed
  const handleModalClose = () => {
    setOpenModal(null);
    setInitialTagGroup(null);
    setSelectedTag(null);
  };

  // Track the existing tags and suggestions into a single object
  const [allTagGroupsWithTags, setAllTagGroupsWithTags] = useState<
    TagGroupWithTags[]
  >([]);

  /**
   * When the tags and tagSuggestions have loaded, combine the data into a single array of tag groups with tags
   */
  useEffect(() => {
    // Wait for all of the tags and suggestions to be loaded
    if (
      !tags?.tagMany ||
      !tagSuggestions?.tagSuggestionMany ||
      tagGroups.length === 0
    )
      return;

    setAllTagGroupsWithTags([
      ..._.orderBy(
        tagGroups.map(tagGroup => ({
          tagGroup,
          tags: _.orderBy(
            tags?.tagMany?.filter(tag => tag.tagGroup?._id === tagGroup._id) ??
              [],
            "name.en",
            "asc",
          ),
        })),
        "tagGroup.name.en",
        "asc",
      ),
      {
        tagGroup: {
          _id: "suggestions",
          key: TagManagerFilter.Suggestions,
          name: { en: "Suggestions" },
          type: TagManagerFilter.Suggestions,
        } as TagGroupSuggestions,
        tags: _.orderBy(
          tagSuggestions?.tagSuggestionMany ?? [],
          "name.en",
          "asc",
        ),
      },
    ]);
    // Set the default filter
    setFilter(TagManagerFilter.BusinessType);
  }, [tags?.tagMany, tagSuggestions?.tagSuggestionMany, tagGroups]);

  /**
   * Get the filtered tag groups with tags and counts
   * - Runs when searchValur or filter changes
   * @returns The filtered tag groups with tags and counts
   */
  const { filteredTagGroupsWithTags, filteredTagGroupsCounts } = useMemo(() => {
    const searchValueLowerCase = (searchValue ?? "").toLowerCase();
    /**
     * Filter the tags by the search value
     * @param tags - The tags to filter
     * @returns The filtered tags
     */
    const getFilteredTags = (
      tags:
        | TagManyWithCountsQuery["tagMany"]
        | TagSuggestionManyQuery["tagSuggestionMany"] = [],
    ) => {
      return tags.filter(tag => {
        if (searchValueLowerCase) {
          return getMultilingualString(tag.name)
            .toLowerCase()
            .includes(searchValueLowerCase);
        }
        return true;
      });
    };

    // Filter to only include the tag groups and tags that match the search value and filter
    const filtered =
      !searchValue && !filter
        ? allTagGroupsWithTags
        : (allTagGroupsWithTags
            .filter(
              tagGroupWithTags => tagGroupWithTags.tagGroup.type === filter,
            )
            .map(tagGroupWithTags => ({
              tagGroup: tagGroupWithTags.tagGroup,
              tags: getFilteredTags(tagGroupWithTags.tags),
            }))
            .filter(
              tagGroupWithTags => tagGroupWithTags.tags.length > 0,
            ) as TagGroupWithTags[]);

    // Count the tags for each tag group
    const counts = new Map<TagManagerFilter, number>();
    allTagGroupsWithTags.forEach(tagGroupWithTags => {
      const tagGroupType = tagGroupWithTags.tagGroup.type as TagManagerFilter;
      const count = counts.get(tagGroupType) ?? 0;
      counts.set(
        tagGroupType,
        count + getFilteredTags(tagGroupWithTags.tags).length,
      );
    });

    return {
      filteredTagGroupsWithTags: filtered,
      filteredTagGroupsCounts: counts,
    };
  }, [allTagGroupsWithTags, searchValue, filter]);

  /**
   * When a tag suggestion is created, add it to the allTagGroupsWithTags array
   * - Since the allTagGroupsWithTags array is re-created here, the filters
   *   will be re-applied
   */
  const handleTagSuggestionCreated = (
    tagSuggestion: TagSuggestionManyQuery["tagSuggestionMany"][0],
  ) =>
    setAllTagGroupsWithTags(prev =>
      prev.map(tagGroupWithTags =>
        tagGroupWithTags.tagGroup.key === TagManagerFilter.Suggestions
          ? {
              ...tagGroupWithTags,
              tags: _.orderBy(
                [...(tagGroupWithTags.tags ?? []), tagSuggestion],
                "name.en",
                "asc",
              ) as TagSuggestionManyQuery["tagSuggestionMany"],
            }
          : tagGroupWithTags,
      ),
    );

  /**
   * When a tag suggestion is approved, the previous suggestion will be removed
   * and the new tag will be added to the appropriate tag group
   * - Since the allTagGroupsWithTags array is re-created here, the filters
   *   will be re-applied
   */
  const handleTagSuggestionApproved = (
    tag: TagManyWithCountsQuery["tagMany"][0],
    tagSuggestion: TagSuggestionManyQuery["tagSuggestionMany"][0],
  ) =>
    setAllTagGroupsWithTags(prev =>
      prev.map(tagGroupWithTags =>
        tagGroupWithTags.tagGroup.key === TagManagerFilter.Suggestions
          ? {
              ...tagGroupWithTags,
              // Remove the suggestion from the tags array
              tags: tagGroupWithTags.tags.filter(
                tag => tag._id !== tagSuggestion._id,
              ) as TagSuggestionManyQuery["tagSuggestionMany"],
            }
          : tagGroupWithTags.tagGroup.key === tag.tagGroup?.key
            ? {
                ...tagGroupWithTags,
                // Add the new tag to the tags array
                tags: _.orderBy(
                  [...(tagGroupWithTags.tags ?? []), tag],
                  "name.en",
                  "asc",
                ) as TagManyWithCountsQuery["tagMany"],
              }
            : tagGroupWithTags,
      ),
    );

  /**
   * When a tag suggestion is deleted, the suggestion will be removed from the appropriate tag group
   * - Since the allTagGroupsWithTags array is re-created here, the filters
   *   will be re-applied
   */
  const handleTagSuggestionDeleted = (tagSuggestionId: string) =>
    setAllTagGroupsWithTags(prev =>
      prev.map(tagGroupWithTags =>
        tagGroupWithTags.tagGroup.key === TagManagerFilter.Suggestions
          ? {
              ...tagGroupWithTags,
              tags: tagGroupWithTags.tags.filter(
                tag => tag._id !== tagSuggestionId,
              ) as TagSuggestionManyQuery["tagSuggestionMany"],
            }
          : tagGroupWithTags,
      ),
    );

  /**
   * When a tag is updated, the tag will be updated in the appropriate tag group
   * - Since the allTagGroupsWithTags array is re-created here, the filters
   *   will be re-applied
   */
  const handleTagUpdated = (tag: TagByIdQuery["tagById"]) => {
    if (!tag) return;

    setAllTagGroupsWithTags(prev =>
      prev.map(tagGroupWithTags =>
        tagGroupWithTags.tagGroup.key === tag.tagGroup?.key
          ? // If the tag group matches, we need to either insert the new tag (if the group was changed)
            // or update the existing tag
            {
              ...tagGroupWithTags,
              tags: _.orderBy(
                _.uniqBy(
                  [
                    ...tagGroupWithTags.tags.map(t =>
                      t._id === tag._id ? tag : t,
                    ),
                    tag,
                  ],
                  "_id",
                ),
                "name.en",
                "asc",
              ) as TagManyWithCountsQuery["tagMany"],
            }
          : // If the tag group doesn't match, we need to ensure the tag does not exist in the tag group
            // (for cases when the tag group was changed)
            {
              ...tagGroupWithTags,
              tags: tagGroupWithTags.tags.filter(
                t => t._id !== tag._id,
              ) as TagManyWithCountsQuery["tagMany"],
            },
      ),
    );
  };

  /**
   * When a tag is deleted, the tag will be removed from the appropriate tag group
   * - Since the allTagGroupsWithTags array is re-created here, the filters
   *   will be re-applied
   */
  const handleTagDeleted = (tag: TagManyWithCountsQuery["tagMany"][0]) =>
    setAllTagGroupsWithTags(prev =>
      prev.map(tagGroupWithTags =>
        tagGroupWithTags.tagGroup.key === tag.tagGroup?.key
          ? {
              ...tagGroupWithTags,
              tags: tagGroupWithTags.tags.filter(
                t => t._id !== tag._id,
              ) as TagManyWithCountsQuery["tagMany"],
            }
          : tagGroupWithTags,
      ),
    );

  /**
   * When the tag split is completed, we need to
   * 1. Update the existing tag's name to the new name
   * 2. Add the new tags to the appropriate tag groups
   * @param updatedTag
   * @param newTags
   */
  const handleTagSplitCompleted = (
    updatedTag: TagByIdQuery["tagById"],
    newTags: TagByIdQuery["tagById"][],
  ) => {
    if (!updatedTag || !newTags || newTags.length === 0) return;

    setAllTagGroupsWithTags(prev =>
      prev.map(tagGroupWithTags =>
        tagGroupWithTags.tagGroup.key === updatedTag.tagGroup?.key
          ? {
              ...tagGroupWithTags,
              tags: _.orderBy(
                [
                  ...tagGroupWithTags.tags.map(tag =>
                    tag._id === updatedTag._id ? updatedTag : tag,
                  ),
                  ...newTags,
                ],
                "name.en",
                "asc",
              ) as TagManyWithCountsQuery["tagMany"],
            }
          : tagGroupWithTags,
      ),
    );
  };

  const [isRefreshingCounts, setIsRefreshingCounts] = useState(false);
  const [tagRefreshCounts] = useTagRefreshCountsLazyQuery();
  /**
   * Call the updateTagUsageJob job to refresh the tag counts
   * - When completed, reload the tags
   */
  const handleRefreshCounts = async () => {
    setIsRefreshingCounts(true);
    const result = await tagRefreshCounts();
    if (result.data?.tagRefreshCounts) {
      await refetchTags();
    }

    setIsRefreshingCounts(false);
  };

  return {
    allTagGroupsWithTags,
    flyoutCategoryOptions,
    filteredTagGroupsWithTags,
    filteredTagGroupsCounts,
    loading: tagsLoading || tagSuggestionsLoading,
    error: tagsError || tagSuggestionsError,
    isRefreshingCounts,
    searchValue,
    filter,
    openModal,
    initialTagGroup,
    selectedTag,
    setOpenModal,
    onSearchValueChange: handleSearchValueChange,
    onFilterChange: handleFilterChange,
    onRefreshCounts: handleRefreshCounts,
    onAddTag: handleAddTag,
    onViewTag: handleViewTag,
    onModalClose: handleModalClose,
    onTagSuggestionCreated: handleTagSuggestionCreated,
    onTagSuggestionApproved: handleTagSuggestionApproved,
    onTagSuggestionDeleted: handleTagSuggestionDeleted,
    onTagUpdated: handleTagUpdated,
    onTagDeleted: handleTagDeleted,
    onTagSplitCompleted: handleTagSplitCompleted,
    // When a tag is merged into another tag, the backend will ensure all entities with
    // references to the initial tag are tagged with the tag it is being merged into.
    // It will then delete the initial tag references and delete the initial tag.
    // Since the other tag still exists, all we need to do here is delete the initial tag.
    onTagMergeCompleted: handleTagDeleted,
  };
};
